const toCents = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round((n + Number.EPSILON) * 100);
};

const fromCents = (cents) => cents / 100;

const getProductCategories = (product) => {
  const cats = product.categories && Array.isArray(product.categories) ? product.categories : [];
  const single = product.category ? [product.category] : [];
  return [...new Set([...single, ...cats])];
};

const keywordMatchesProduct = (product, rule) => {
  const kw = String(rule.keyword || '').trim().toLowerCase();
  if (!kw) return false;
  const matchModel = product.model && String(product.model).toLowerCase().includes(kw);
  const matchCategory = getProductCategories(product).some((cat) => String(cat || '').toLowerCase().includes(kw));
  return matchModel || matchCategory;
};

const emptyResult = () => ({
  items: [],
  totals: {
    original: 0,
    discounted: 0,
    savings: 0,
    discountPercentage: 0
  },
  itemCount: 0,
  subtotal: 0,
  subtotalCents: 0,
  discountAmount: 0,
  discountCents: 0,
  discountDetail: '',
  discountableSubtotal: 0,
  linesById: {}
});

const detailFor = (type, percentage) => {
  if (type === 'keyword') return `Descuento aplicado (${percentage}%) por palabra clave`;
  if (type === 'quantity') return `Descuento aplicado (${percentage}%) por cantidad mínima`;
  if (type === 'amount') return `Descuento aplicado (${percentage}%) por monto mínimo`;
  return '';
};

/**
 * Descuento por línea, con las mismas reglas que el resumen mayorista:
 * - un producto excluido no entra en el descuento;
 * - la cantidad mínima se evalúa en esa línea, no sumando el carrito;
 * - la palabra clave aplica si el modelo o la categoría la contienen;
 * - el monto mínimo usa el subtotal de todo el carrito y solo gana si su porcentaje es mayor;
 * - un código cargado a mano compite por línea y no pisa un porcentaje mejor.
 * El importe se redondea a centavos por línea para que el descuento sea la suma de esas líneas.
 */
export function computeCartDiscounts(cart, discountRules, options = {}) {
  const itemsIn = Array.isArray(cart) ? cart : [];
  if (itemsIn.length === 0) return emptyResult();

  const rules = Array.isArray(discountRules) ? discountRules : [];
  const applied = options.appliedDiscount;

  const normalized = itemsIn.map((item) => {
    const quantity = Number(item.quantity) || 0;
    const unitCents = toCents(item.price);
    const lineCents = unitCents * quantity;
    return { item, quantity, unitCents, lineCents };
  });

  const subtotalCents = normalized.reduce((sum, line) => sum + line.lineCents, 0);

  const bestAmount = rules
    .filter((rule) => {
      const minAmount = Number(rule.minAmount);
      return rule.type === 'amount' && Number.isFinite(minAmount) && subtotalCents >= toCents(minAmount);
    })
    .sort((a, b) => Number(b.percentage) - Number(a.percentage))[0] || null;

  let discountCents = 0;
  let discountableCents = 0;

  const items = normalized.map(({ item, quantity, unitCents, lineCents }) => {
    const listUnit = fromCents(unitCents);
    if (!item.discountExcluded) discountableCents += lineCents;

    if (item.discountExcluded || lineCents <= 0) {
      return {
        ...item,
        quantity,
        bestDiscount: { percentage: 0, price: listUnit, description: 'Sin descuento', type: 'none' },
        originalTotal: fromCents(lineCents),
        discountedTotal: fromCents(lineCents),
        savings: 0
      };
    }

    let best = { percentage: 0, type: 'none', description: 'Sin descuento' };

    rules.forEach((rule) => {
      const percentage = Number(rule.percentage) || 0;
      if (percentage <= best.percentage) return;

      if (rule.type === 'keyword' && keywordMatchesProduct(item, rule)) {
        best = {
          percentage,
          type: 'keyword',
          description: `Por palabra clave "${rule.keyword}"`
        };
      } else if (rule.type === 'quantity' && quantity >= Number(rule.minQuantity)) {
        best = {
          percentage,
          type: 'quantity',
          description: `Por cantidad mínima (${rule.minQuantity}+)`
        };
      }
    });

    if (applied && applied.type === 'keyword') {
      const percentage = Number(applied.percentage) || 0;
      if (percentage > best.percentage) {
        best = {
          percentage,
          type: 'keyword',
          description: `Por palabra clave "${applied.keyword}"`
        };
      }
    }

    if (bestAmount && Number(bestAmount.percentage) > best.percentage) {
      const percentage = Number(bestAmount.percentage);
      best = {
        percentage,
        type: 'amount',
        description: `Por monto total (${bestAmount.minAmount}+)`
      };
    }

    const savingsCents = Math.min(
      lineCents,
      Math.max(0, Math.round(lineCents * best.percentage / 100))
    );
    const discountedCents = lineCents - savingsCents;
    discountCents += savingsCents;

    return {
      ...item,
      quantity,
      bestDiscount: {
        percentage: best.percentage,
        price: quantity > 0 ? fromCents(discountedCents) / quantity : listUnit,
        description: best.description,
        type: best.type
      },
      originalTotal: fromCents(lineCents),
      discountedTotal: fromCents(discountedCents),
      savings: fromCents(savingsCents)
    };
  });

  const discountedCents = subtotalCents - discountCents;

  const active = items.filter((item) => item.savings > 0);
  let discountDetail = '';
  if (active.length === 1 || (active.length > 1 && active.every((item) => (
    item.bestDiscount.percentage === active[0].bestDiscount.percentage &&
    item.bestDiscount.type === active[0].bestDiscount.type
  )))) {
    if (active.length > 0) {
      discountDetail = detailFor(active[0].bestDiscount.type, active[0].bestDiscount.percentage);
    }
  } else if (active.length > 1) {
    discountDetail = 'Descuentos aplicados';
  }

  const linesById = {};
  items.forEach((item) => {
    if (item.id != null) linesById[item.id] = item;
  });

  const original = fromCents(subtotalCents);
  const savings = fromCents(discountCents);

  return {
    items,
    totals: {
      original,
      discounted: fromCents(discountedCents),
      savings,
      discountPercentage: subtotalCents > 0 ? (discountCents / subtotalCents) * 100 : 0
    },
    itemCount: normalized.reduce((sum, line) => sum + line.quantity, 0),
    subtotal: original,
    subtotalCents,
    discountAmount: savings,
    discountCents,
    discountDetail,
    discountableSubtotal: fromCents(discountableCents),
    linesById
  };
}

/** Parte una línea en precios unitarios de 2 decimales que suman el total exacto. */
export function toMercadoPagoLines({ title, id, quantity, total }) {
  const qty = Math.max(0, Math.round(Number(quantity) || 0));
  const totalCents = Math.max(0, toCents(total));
  if (qty <= 0) return [];

  const unitCents = Math.floor(totalCents / qty);
  const extraUnits = totalCents - unitCents * qty;
  const regularUnits = qty - extraUnits;
  const lines = [];

  if (regularUnits > 0) {
    lines.push({
      title: String(title),
      id: String(id),
      quantity: regularUnits,
      unit_price: unitCents / 100
    });
  }
  if (extraUnits > 0) {
    lines.push({
      title: String(title),
      id: String(id),
      quantity: extraUnits,
      unit_price: (unitCents + 1) / 100
    });
  }
  return lines;
}
